// B5 · Pie del editor de movimientos (H69, H79) y cuenta por defecto (H67).
//
// Lógica pura: qué dice el botón principal, si se puede pulsar y por qué no,
// y qué cuenta lleva un gasto nuevo.

/** Acción del botón principal del pie. */
export type AccionPie = 'anadir' | 'guardar' | 'guardarYAprobar';

/** Por qué no se puede guardar todavía (se pinta encima del botón). */
export type MotivoBloqueo = 'cuenta' | 'importe';

export type EstadoPie = {
  accion: AccionPie;
  /** Con motivo, el botón sale deshabilitado y el motivo encima. */
  motivo: MotivoBloqueo | null;
  /**
   * Por revisar sin poder aprobarse (falta la categoría en una cuenta del
   * presupuesto): el botón dice «Guardar» y encima se avisa.
   */
  avisoSinCategoria: boolean;
};

type EntradaPie = {
  isAdding: boolean;
  account: string | null | undefined;
  amount: number;
  /** Abierto desde Revisar y aún sin aprobar. */
  porRevisar: boolean;
  /** `puedeAprobarse` (revisar.ts) con lo que hay en el editor. */
  aprobable: boolean;
};

export function estadoPie({
  isAdding,
  account,
  amount,
  porRevisar,
  aprobable,
}: EntradaPie): EstadoPie {
  const motivo: MotivoBloqueo | null = !account
    ? 'cuenta'
    : // Un movimiento nuevo de 0 € es casi siempre un descuido; uno que ya
      // existe con 0 € se puede seguir guardando.
      isAdding && amount === 0
      ? 'importe'
      : null;
  if (isAdding) {
    return { accion: 'anadir', motivo, avisoSinCategoria: false };
  }
  if (porRevisar && aprobable) {
    return { accion: 'guardarYAprobar', motivo, avisoSinCategoria: false };
  }
  return {
    accion: 'guardar',
    motivo,
    avisoSinCategoria: porRevisar && !aprobable,
  };
}

/** Clave de localStorage con el id de la última cuenta usada al añadir. */
export const CLAVE_ULTIMA_CUENTA = 'rumbo-ultima-cuenta';

export function leerUltimaCuenta(): string | null {
  try {
    const id = window.localStorage.getItem(CLAVE_ULTIMA_CUENTA);
    return id || null;
  } catch {
    return null;
  }
}

export function guardarUltimaCuenta(id: string | null | undefined): void {
  if (!id) {
    return;
  }
  try {
    window.localStorage.setItem(CLAVE_ULTIMA_CUENTA, id);
  } catch {
    // Sin almacenamiento (modo privado): no se recuerda.
  }
}

type EntradaCuenta = {
  /** `?account=` de la URL (ya resuelto a id). */
  deUrl?: string | null;
  /** Cuenta desde la que se abrió (estado de la navegación). */
  deNavegacion?: string | null;
  /** Cuenta del último movimiento apuntado en esta sesión. */
  delUltimo?: string | null;
  /** Última cuenta usada, recordada en localStorage. */
  recordada?: string | null;
  /** Cuentas abiertas que se pueden usar. */
  abiertas: ReadonlyArray<{ id: string }>;
};

/**
 * Cuenta de un movimiento nuevo: la explícita (URL o pantalla de origen) y,
 * si no, la última usada, siempre que siga abierta.
 */
export function cuentaPorDefecto({
  deUrl,
  deNavegacion,
  delUltimo,
  recordada,
  abiertas,
}: EntradaCuenta): string | null {
  if (deUrl) {
    return deUrl;
  }
  if (deNavegacion) {
    return deNavegacion;
  }
  const existe = (id: string | null | undefined): id is string =>
    !!id && abiertas.some(a => a.id === id);
  if (existe(delUltimo)) {
    return delUltimo;
  }
  if (existe(recordada)) {
    return recordada;
  }
  return null;
}
